<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount, shallowRef, nextTick } from 'vue';
import { useDebounceFn, useMediaQuery } from '@vueuse/core';
import axios from 'axios';
import http from '@/services/http';
import { usePersistentTable } from '@/composables/usePersistentTable';
import type { GeoRadiusPin } from '@/stores/persistentTableStore';
import { parsePhysicalAddressParts } from '@/utils/physicalAddressParts';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const props = defineProps<{
  rows: Record<string, unknown>[] | undefined;
  isLoading: boolean;
}>();

const resultRows = computed(() => props.rows ?? []);

function parseRowGeoCoordinates(row: Record<string, unknown>): { lat: number; lon: number } | null {
  const raw = row.geo_location;
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed !== '' && trimmed.includes(',')) {
      const parts = trimmed.split(',', 2).map((p) => p.trim());
      if (parts.length === 2) {
        const lat = Number(parts[0]);
        const lon = Number(parts[1]);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return { lat, lon };
        }
      }
    }
    return null;
  }
  if (Array.isArray(raw) && raw.length >= 2) {
    const lon = Number(raw[0]);
    const lat = Number(raw[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      // Elasticsearch geo_point arrays are [lon, lat]
      return { lat, lon };
    }
    return null;
  }
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const o = raw as { lat?: unknown; lon?: unknown; lng?: unknown };
    const lat = typeof o.lat === 'number' ? o.lat : Number(o.lat);
    const lonRaw = o.lon ?? o.lng;
    const lon = typeof lonRaw === 'number' ? lonRaw : Number(lonRaw);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat, lon };
    }
  }
  return null;
}

function rowHasGeo(row: Record<string, unknown>): boolean {
  return parseRowGeoCoordinates(row) !== null;
}

function firstGeoRowIndex(rows: Record<string, unknown>[] | undefined): number | null {
  const list = rows ?? [];
  for (let index = 0; index < list.length; index += 1) {
    if (rowHasGeo(list[index])) {
      return index;
    }
  }
  return null;
}

/** Rows that supply coordinates for map markers (mirrors `syncResultAnnotations` logic). */
const geoPinSourceRowCount = computed(
  () => resultRows.value.filter((row) => parseRowGeoCoordinates(row) !== null).length,
);

function rowDisplayString(row: Record<string, unknown>): string {
  return typeof row.display_string === 'string' ? row.display_string : String(row.id ?? '');
}

function rowPhysicalAddress(row: Record<string, unknown>): string {
  return typeof row.physical_address === 'string' ? row.physical_address.trim() : '';
}

const ADDRESS_FIELD_KEYS = ['address1', 'address2', 'city', 'stateSubdivision', 'zipPostal', 'country'] as const;

const { setGeoRadiusFilter, mapCardFieldVisibility } = usePersistentTable();

const anyAddressFieldVisible = computed(() => {
  const visibility = mapCardFieldVisibility.value;
  return ADDRESS_FIELD_KEYS.some((key) => visibility[key]);
});

function visibleAddressLines(row: Record<string, unknown>): { key: string; text: string }[] {
  const raw = rowPhysicalAddress(row);
  if (!raw) {
    return [];
  }
  const parts = parsePhysicalAddressParts(raw);
  const visibility = mapCardFieldVisibility.value;
  const lines: { key: string; text: string }[] = [];

  const visible = (key: (typeof ADDRESS_FIELD_KEYS)[number]) =>
    visibility[key] && parts[key]?.trim();

  // Address line 1 — optionally merge short address2 onto the same line
  const address1 = visible('address1') ? parts.address1!.trim() : '';
  const address2 = visible('address2') ? parts.address2!.trim() : '';
  if (address1 && address2 && address2.length < 10) {
    lines.push({ key: 'address1', text: `${address1}, ${address2}` });
  } else {
    if (address1) lines.push({ key: 'address1', text: address1 });
    if (address2) lines.push({ key: 'address2', text: address2 });
  }

  // City, state, zip — combine onto one line
  const cityStateZipParts: string[] = [];
  for (const key of ['city', 'stateSubdivision', 'zipPostal'] as const) {
    const text = visible(key) ? parts[key]!.trim() : '';
    if (text) cityStateZipParts.push(text);
  }
  if (cityStateZipParts.length) {
    lines.push({ key: 'cityStateZip', text: cityStateZipParts.join(', ') });
  }

  // Country on its own line
  const country = visible('country') ? parts.country!.trim() : '';
  if (country) {
    lines.push({ key: 'country', text: country });
  }

  return lines;
}

const SPLIT_MIN_PERCENT = 16;
const SPLIT_MAX_PERCENT = 50;
const COLLAPSE_BELOW_PERCENT = 11;
const DEFAULT_PANEL_PERCENT = 42;

const resultsListOpen = ref(true);
const resultsPanelWidthPercent = ref(DEFAULT_PANEL_PERCENT);
const panelWidthBeforeCollapse = ref(DEFAULT_PANEL_PERCENT);
const splitRowRef = ref<HTMLElement | null>(null);
const isMdUp = useMediaQuery('(min-width: 768px)');

const asideLayoutStyle = computed(() => {
  if (!isMdUp.value) {
    return {};
  }
  if (!resultsListOpen.value) {
    return { flex: '0 0 0px', minWidth: '0', overflow: 'hidden' } as Record<string, string>;
  }
  const capped = Math.min(
    SPLIT_MAX_PERCENT,
    Math.max(SPLIT_MIN_PERCENT, resultsPanelWidthPercent.value),
  );
  return { flex: `0 0 ${capped}%`, minWidth: '0' } as Record<string, string>;
});

/** Slider value: 0 when the list is hidden; otherwise panel width percent (for assistive tech). */
const resultsPaneSliderValueNow = computed(() =>
  resultsListOpen.value ? Math.round(resultsPanelWidthPercent.value) : 0,
);

const resultsPaneSliderAriaValueText = computed(() =>
  resultsListOpen.value ? undefined : 'Hidden',
);

function pointerToPanelPercent(clientX: number): number {
  const row = splitRowRef.value;
  if (!row) {
    return resultsPanelWidthPercent.value;
  }
  const rect = row.getBoundingClientRect();
  if (rect.width <= 0) {
    return resultsPanelWidthPercent.value;
  }
  return ((clientX - rect.left) / rect.width) * 100;
}

function clampPanelPercent(percent: number): number {
  return Math.min(SPLIT_MAX_PERCENT, Math.max(SPLIT_MIN_PERCENT, percent));
}

function onPaneSliderPointerDown(event: PointerEvent): void {
  if (!isMdUp.value || event.button !== 0) {
    return;
  }
  const target = event.currentTarget as HTMLElement;
  target.setPointerCapture(event.pointerId);

  const startX = event.clientX;
  let didSignificantDrag = false;

  const onPointerMove = (ev: PointerEvent): void => {
    if (Math.abs(ev.clientX - startX) > 4) {
      didSignificantDrag = true;
    }
    const rawPercent = pointerToPanelPercent(ev.clientX);
    if (!resultsListOpen.value) {
      if (rawPercent >= COLLAPSE_BELOW_PERCENT) {
        resultsListOpen.value = true;
        resultsPanelWidthPercent.value = clampPanelPercent(rawPercent);
        panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
      }
    } else if (rawPercent < COLLAPSE_BELOW_PERCENT) {
      resultsListOpen.value = false;
    } else {
      resultsPanelWidthPercent.value = clampPanelPercent(rawPercent);
      panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
    }
    nextTick(() => requestMapKitLayoutReflow());
  };

  const onPointerUp = (ev: PointerEvent): void => {
    target.releasePointerCapture(ev.pointerId);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);

    if (!didSignificantDrag) {
      if (resultsListOpen.value) {
        panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
        resultsListOpen.value = false;
      } else {
        resultsListOpen.value = true;
        resultsPanelWidthPercent.value = clampPanelPercent(panelWidthBeforeCollapse.value);
      }
      nextTick(() => requestMapKitLayoutReflow());
    }
  };

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
}

function onPaneSliderKeydown(event: KeyboardEvent): void {
  if (!isMdUp.value) {
    return;
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    if (resultsListOpen.value) {
      panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
      resultsListOpen.value = false;
    } else {
      resultsListOpen.value = true;
      resultsPanelWidthPercent.value = clampPanelPercent(panelWidthBeforeCollapse.value);
    }
    nextTick(() => requestMapKitLayoutReflow());
    return;
  }
  if (!resultsListOpen.value) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      resultsListOpen.value = true;
      resultsPanelWidthPercent.value = clampPanelPercent(panelWidthBeforeCollapse.value);
      nextTick(() => requestMapKitLayoutReflow());
    }
    return;
  }
  const step = event.shiftKey ? 8 : 3;
  if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
    event.preventDefault();
    resultsPanelWidthPercent.value = clampPanelPercent(resultsPanelWidthPercent.value - step);
    panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
    nextTick(() => requestMapKitLayoutReflow());
  } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
    event.preventDefault();
    resultsPanelWidthPercent.value = clampPanelPercent(resultsPanelWidthPercent.value + step);
    panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
    nextTick(() => requestMapKitLayoutReflow());
  } else if (event.key === 'Home') {
    event.preventDefault();
    resultsPanelWidthPercent.value = SPLIT_MIN_PERCENT;
    panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
    nextTick(() => requestMapKitLayoutReflow());
  } else if (event.key === 'End') {
    event.preventDefault();
    resultsPanelWidthPercent.value = SPLIT_MAX_PERCENT;
    panelWidthBeforeCollapse.value = resultsPanelWidthPercent.value;
    nextTick(() => requestMapKitLayoutReflow());
  }
}

const mapContainerRef = ref<HTMLElement | null>(null);
/** Incremented on each MapKit `region-change-end` (camera idle). Used by e2e to wait after `showItems` / pan. */
const mapRegionSettledEpoch = ref(0);
const mapRef = shallowRef<unknown>(null);
const initError = ref<string | null>(null);
const tokenError = ref(false);
let mapResizeObserver: ResizeObserver | null = null;

type MapPin = GeoRadiusPin & { _clientId: string };
const droppedPins = ref<MapPin[]>([]);

// With click-to-drop-pin (see `initMap`):
// const DEFAULT_RADIUS_METERS = 8000;
// function newClientId(): string {
//   return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
// }

const pushPinsToStore = useDebounceFn((pins: MapPin[]) => {
  const payload: GeoRadiusPin[] = pins.map(({ latitude, longitude, radiusMeters }) => ({
    latitude,
    longitude,
    radiusMeters,
  }));
  setGeoRadiusFilter(payload.length > 0 ? payload : null);
}, 400);

watch(droppedPins, (pins) => void pushPinsToStore(pins), { deep: true });

function updatePinRadius(clientId: string, radiusMeters: number) {
  const pin = droppedPins.value.find((p) => p._clientId === clientId);
  if (pin) {
    pin.radiusMeters = Math.max(100, Math.min(200_000, Math.round(radiusMeters)));
  }
}

function removePin(clientId: string) {
  droppedPins.value = droppedPins.value.filter((p) => p._clientId !== clientId);
}

const resultAnnotations = shallowRef<MapKitAnnotationLike[]>([]);
/** Row index in the current page → marker for that row (only rows with geo). */
const rowIndexToResultAnnotation = shallowRef(new Map<number, MapKitAnnotationLike>());
const focusedRowIndex = ref<number | null>(null);
const circleOverlays = shallowRef<MapKitOverlayLike[]>([]);

type MapKitAnnotationLike = { __mk?: true };
type MapKitOverlayLike = { __mk?: true };

type MapKitGlobal = {
  init: (options: { authorizationCallback: (done: (token: string) => void) => void }) => void;
  Coordinate: new (lat: number, lng: number) => unknown;
  CoordinateRegion: new (center: unknown, span: unknown) => unknown;
  CoordinateSpan: new (latDelta: number, lngDelta: number) => unknown;
  Map: new (element: HTMLElement, options: Record<string, unknown>) => unknown;
  MarkerAnnotation: new (coordinate: unknown, options: Record<string, unknown>) => MapKitAnnotationLike;
  CircleOverlay: new (coordinate: unknown, radiusMeters: number) => MapKitOverlayLike & { style?: unknown };
  Style: new (options: Record<string, unknown>) => unknown;
  FeatureVisibility: { Hidden: string };
};

/** MapKit JS must only call `mapkit.init` once per page; repeat calls break a remounted map (blank tiles). */
let mapKitInitOncePromise: Promise<{ token503: boolean }> | null = null;

function initMapKitOnce(): Promise<{ token503: boolean }> {
  if (mapKitInitOncePromise) {
    return mapKitInitOncePromise;
  }
  mapKitInitOncePromise = (async (): Promise<{ token503: boolean }> => {
    await loadMapKitScript();
    const mk = (window as unknown as Window & { mapkit: MapKitGlobal }).mapkit;
    let token503 = false;
    await new Promise<void>((resolve) => {
      mk.init({
        authorizationCallback(done: (token: string) => void) {
          void http
            .get<{ token: string }>('/api/mapkit-js-token')
            .then((response) => {
              done(response.data.token);
            })
            .catch((error: unknown) => {
              if (axios.isAxiosError(error) && error.response?.status === 503) {
                token503 = true;
              }
              done('');
            })
            .finally(() => resolve());
        },
      });
    });
    return { token503 };
  })();
  return mapKitInitOncePromise;
}

function loadMapKitScript(): Promise<void> {
  const globalWindow = window as Window & { mapkit?: unknown };
  if (globalWindow.mapkit) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-mapkit-js="1"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('MapKit load failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.apple-mapkit.com/mk/5.78.1/mapkit.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.mapkitJs = '1';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('MapKit script failed'));
    document.head.appendChild(script);
  });
}

function removeResultAnnotations() {
  const map = mapRef.value as MapWithAnnotations | null;
  if (!map) {
    return;
  }
  for (const annotation of resultAnnotations.value) {
    map.removeAnnotation(annotation);
  }
  resultAnnotations.value = [];
}

function removeCircleOverlays() {
  const map = mapRef.value as MapWithOverlays | null;
  if (!map) {
    return;
  }
  for (const overlay of circleOverlays.value) {
    map.removeOverlay(overlay);
  }
  circleOverlays.value = [];
}

type MapWithAnnotations = { removeAnnotation: (a: MapKitAnnotationLike) => void; addAnnotation: (a: MapKitAnnotationLike) => void };
type MapWithOverlays = { removeOverlay: (o: MapKitOverlayLike) => void; addOverlay: (o: MapKitOverlayLike) => void };
type MapWithShowItems = { showItems?: (items: MapKitAnnotationLike[]) => void };

function syncResultAnnotations() {
  const map = mapRef.value as (MapWithAnnotations & MapWithOverlays) | null;
  const mk = (window as Window & { mapkit?: MapKitGlobal }).mapkit;
  if (!map || !mk) {
    return;
  }
  removeResultAnnotations();
  const next: MapKitAnnotationLike[] = [];
  const indexToAnnotation = new Map<number, MapKitAnnotationLike>();
  const rows = props.rows ?? [];
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const geo = parseRowGeoCoordinates(row);
    if (geo == null) {
      continue;
    }
    const coordinate = new mk.Coordinate(geo.lat, geo.lon);
    const title =
      typeof (row as { display_string?: string }).display_string === 'string'
        ? (row as { display_string: string }).display_string
        : String(row.id ?? '');
    const annotation = new mk.MarkerAnnotation(coordinate, {
      title,
      color: '#6366f1',
    });
    map.addAnnotation(annotation);
    next.push(annotation);
    indexToAnnotation.set(rowIndex, annotation);
  }
  resultAnnotations.value = next;
  rowIndexToResultAnnotation.value = indexToAnnotation;
  const mapForShow = map as MapWithShowItems;
  if (next.length > 0 && typeof mapForShow.showItems === 'function') {
    // Zoom to the first result on this page that has coordinates (not the full-page bbox).
    mapForShow.showItems([next[0]]);
    focusedRowIndex.value = firstGeoRowIndex(rows);
  } else {
    focusedRowIndex.value = null;
  }
}

function focusResultOnMap(rowIndex: number): void {
  const row = resultRows.value[rowIndex];
  if (!row || !rowHasGeo(row)) {
    return;
  }
  const map = mapRef.value as MapWithShowItems | null;
  const annotation = rowIndexToResultAnnotation.value.get(rowIndex);
  if (!map || !annotation || typeof map.showItems !== 'function') {
    return;
  }
  map.showItems([annotation]);
  focusedRowIndex.value = rowIndex;
}

function syncRadiusCircles() {
  const map = mapRef.value as (MapWithAnnotations & MapWithOverlays) | null;
  const mk = (window as Window & { mapkit?: MapKitGlobal }).mapkit;
  if (!map || !mk) {
    return;
  }
  removeCircleOverlays();
  const next: MapKitOverlayLike[] = [];
  for (const pin of droppedPins.value) {
    const coordinate = new mk.Coordinate(pin.latitude, pin.longitude);
    const circle = new mk.CircleOverlay(coordinate, pin.radiusMeters);
    if (typeof mk.Style === 'function') {
      circle.style = new mk.Style({
        fillColor: 'rgba(99, 102, 241, 0.12)',
        strokeColor: 'rgba(99, 102, 241, 0.75)',
        lineWidth: 2,
      });
    }
    map.addOverlay(circle);
    next.push(circle);
  }
  circleOverlays.value = next;
}

watch(
  () => props.rows,
  () => {
    if (mapRef.value) {
      syncResultAnnotations();
    }
  },
  { deep: true },
);

watch(droppedPins, () => syncRadiusCircles(), { deep: true });

function requestMapKitLayoutReflow(): void {
  window.dispatchEvent(new Event('resize'));
}

watch(resultsListOpen, () => {
  nextTick(() => requestMapKitLayoutReflow());
});

async function initMap() {
  initError.value = null;
  tokenError.value = false;
  await nextTick();
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
  const container = mapContainerRef.value;
  if (!container) {
    return;
  }
  try {
    const { token503 } = await initMapKitOnce();
    if (token503) {
      tokenError.value = true;
      return;
    }

    const mk = (window as unknown as Window & { mapkit: MapKitGlobal }).mapkit;

    const center = new mk.Coordinate(39.8283, -98.5795);
    const span = new mk.CoordinateSpan(25, 25);
    const region = new mk.CoordinateRegion(center, span);
    const featureVisibility = (mk as { FeatureVisibility?: { Hidden: string } }).FeatureVisibility;
    const map = new mk.Map(container, {
      region,
      showsCompass: featureVisibility?.Hidden ?? 'hidden',
    }) as {
      addEventListener: (event: string, handler: (e: { pointOnPage?: { x: number; y: number } }) => void) => void;
      convertPointOnPageToCoordinate: (p: { x: number; y: number }) => { latitude: number; longitude: number };
      destroy?: () => void;
    };

    map.addEventListener('region-change-end', () => {
      mapRegionSettledEpoch.value += 1;
      container.setAttribute('data-e2e-map-region-settled', String(mapRegionSettledEpoch.value));
    });

    // Click-to-drop-pin disabled for now.
    // map.addEventListener('single-tap', (event) => {
    //   if (!event.pointOnPage) {
    //     return;
    //   }
    //   const coord = map.convertPointOnPageToCoordinate(event.pointOnPage);
    //   droppedPins.value = [
    //     ...droppedPins.value,
    //     {
    //       _clientId: newClientId(),
    //       latitude: coord.latitude,
    //       longitude: coord.longitude,
    //       radiusMeters: DEFAULT_RADIUS_METERS,
    //     },
    //   ];
    // });

    mapRef.value = map;
    mapResizeObserver?.disconnect();
    mapResizeObserver = new ResizeObserver(() => {
      requestMapKitLayoutReflow();
    });
    mapResizeObserver.observe(container);
    requestMapKitLayoutReflow();
    syncResultAnnotations();
    syncRadiusCircles();
  } catch (error: unknown) {
    initError.value = error instanceof Error ? error.message : 'Map failed to load';
  }
}

onMounted(() => {
  void initMap();
});

onBeforeUnmount(() => {
  setGeoRadiusFilter(null);
  mapResizeObserver?.disconnect();
  mapResizeObserver = null;
  const map = mapRef.value as { destroy?: () => void; removeAnnotation?: (a: MapKitAnnotationLike) => void; removeOverlay?: (o: MapKitOverlayLike) => void } | null;
  if (map) {
    removeResultAnnotations();
    removeCircleOverlays();
    map.destroy?.();
  }
  mapRef.value = null;
});
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-3" data-testid="search-map-view">
    <span class="sr-only" data-testid="search-map-geo-pins-count">{{ geoPinSourceRowCount }}</span>
    <div
      v-if="tokenError"
      class="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
      role="status"
    >
      MapKit is not configured on the server. Set MAPKIT_* in the API environment, or use List view.
    </div>
    <div
      v-else-if="initError"
      class="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm"
      role="alert"
    >
      {{ initError }}
    </div>

    <div v-if="isLoading" class="text-sm text-muted-foreground">Updating results…</div>

    <div
      v-if="
        !tokenError &&
        !initError &&
        !isLoading &&
        resultRows.length > 0 &&
        geoPinSourceRowCount === 0
      "
      class="rounded-md border border-muted bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
      role="status"
      data-testid="search-map-no-geo-hint"
    >
      None of these search results include coordinates for the map.
    </div>

    <div v-if="droppedPins.length" class="space-y-2 rounded-md border bg-card p-3 text-sm">
      <div v-for="pin in droppedPins" :key="pin._clientId" class="flex flex-wrap items-center gap-2">
        <span class="text-muted-foreground shrink-0">Radius (m)</span>
        <Input
          class="w-28"
          type="number"
          :model-value="String(pin.radiusMeters)"
          min="100"
          max="200000"
          @update:model-value="(v) => updatePinRadius(pin._clientId, Number(v))"
        />
        <Button type="button" variant="ghost" size="sm" @click="removePin(pin._clientId)">Remove</Button>
      </div>
    </div>

    <div
      ref="splitRowRef"
      class="flex min-h-0 min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-stretch"
      data-testid="search-map-with-results"
    >
      <aside
        class="order-1 flex min-h-0 min-w-0 flex-1 flex-col gap-2 md:order-1 md:h-full md:max-h-full"
        :style="asideLayoutStyle"
        aria-label="Search results"
      >
        <div
          v-if="!resultsListOpen"
          class="flex shrink-0 items-center justify-center md:hidden"
        >
          <span class="sr-only">Result list is hidden.</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            class="shrink-0"
            data-testid="search-map-results-list-toggle"
            :aria-expanded="resultsListOpen"
            aria-controls="search-map-results-list"
            aria-label="Show result list"
            @click="resultsListOpen = !resultsListOpen"
          >
            <span class="inline-flex" aria-hidden="true">
              <ChevronDownIcon class="size-5" />
            </span>
          </Button>
        </div>
        <div
          v-show="resultsListOpen"
          class="flex min-h-0 min-w-0 flex-1 flex-row items-start gap-1 md:h-full md:min-h-0 md:flex-col md:gap-0"
        >
          <div
            id="search-map-results-list"
            class="min-h-0 min-w-0 flex-1 space-y-2 overflow-y-auto overscroll-contain md:h-full md:w-full"
          >
            <p
              v-if="!resultRows.length && !isLoading"
              class="max-w-[min(100%,22rem)] rounded-md border border-dashed bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground md:max-w-none"
            >
              No results in this page.
            </p>
            <article
              v-for="(row, index) in resultRows"
              :key="`${row.id ?? 'row'}-${index}`"
              class="w-full max-w-[min(100%,22rem)] rounded-md border bg-card px-3 py-2.5 text-sm shadow-sm transition-colors md:max-w-none"
              :class="[
                rowHasGeo(row)
                  ? 'cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  : 'cursor-default',
                focusedRowIndex === index ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : '',
              ]"
              :role="rowHasGeo(row) ? 'button' : undefined"
              :tabindex="rowHasGeo(row) ? 0 : undefined"
              :aria-current="rowHasGeo(row) && focusedRowIndex === index ? 'true' : undefined"
              data-testid="search-map-result-item"
              :data-has-geo="rowHasGeo(row) ? 'true' : 'false'"
              @click="focusResultOnMap(index)"
              @keydown.enter.prevent="focusResultOnMap(index)"
              @keydown.space.prevent="focusResultOnMap(index)"
            >
              <div class="font-medium leading-snug text-foreground">
                {{ rowDisplayString(row) }}
              </div>
              <div
                v-if="anyAddressFieldVisible"
                class="mt-2 border-l-2 border-muted-foreground/25 pl-2.5 text-[13px] leading-relaxed tracking-wide text-muted-foreground"
              >
                <template v-if="visibleAddressLines(row).length">
                  <div
                    v-for="line in visibleAddressLines(row)"
                    :key="line.key"
                    class="whitespace-pre-wrap break-words"
                  >
                    {{ line.text }}
                  </div>
                </template>
                <template v-else>
                  —
                </template>
              </div>
            </article>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            class="shrink-0 self-start pt-2.5 md:hidden"
            data-testid="search-map-results-list-toggle"
            :aria-expanded="resultsListOpen"
            aria-controls="search-map-results-list"
            aria-label="Hide result list"
            @click="resultsListOpen = !resultsListOpen"
          >
            <span class="inline-flex" aria-hidden="true">
              <ChevronUpIcon class="size-5" />
            </span>
          </Button>
        </div>
      </aside>
      <div
        role="slider"
        aria-orientation="horizontal"
        tabindex="0"
        class="relative z-10 order-2 hidden w-3 min-h-0 shrink-0 cursor-col-resize touch-none select-none md:order-2 md:flex md:flex-col md:items-center md:justify-center md:self-stretch"
        data-testid="search-map-results-pane-separator"
        aria-label="Search result list width"
        aria-controls="search-map-results-list"
        :aria-valuenow="resultsPaneSliderValueNow"
        aria-valuemin="0"
        :aria-valuemax="SPLIT_MAX_PERCENT"
        :aria-valuetext="resultsPaneSliderAriaValueText"
        @pointerdown="onPaneSliderPointerDown"
        @keydown="onPaneSliderKeydown"
      >
        <div
          aria-hidden="true"
          class="min-h-[120px] w-px flex-1 rounded-full bg-border transition-colors hover:bg-primary/50 active:bg-primary md:min-h-0"
        />
      </div>
      <div
        ref="mapContainerRef"
        data-testid="search-map-container"
        class="order-2 h-[40vh] min-h-[260px] w-full min-w-0 shrink-0 rounded-md border bg-muted/30 md:order-3 md:h-full md:max-h-none md:min-h-0 md:flex-1"
        aria-label="Client map"
      />
    </div>
  </div>
</template>
